import { type KitColourRole, type KitPalette, kitColourRoles } from "@nexxt/contracts/crm";

// Brands, the place of Guestlist (ADR-306 §9), pure: the brands as the list shows them (mine first, then Tomorrow's
// own, then the others by name), what each one has (its emails, its campaign of the moment), the key of a brand in a
// URL, and the colours of its kit on its page.

/** Tomorrow's own emails and kit are of no brand (`null` in crm); in a URL it has a name of its own. */
export const TOMORROW_KEY = "tomorrow";

/** The sections of the page of a brand, in the URL (`tab`); the Look without one. */
export const brandTabs = ["look", "emails", "campaigns", "buyers"] as const;
export type BrandTab = (typeof brandTabs)[number];

/** The key of a brand in the URL of its page: its id, or `tomorrow`. */
export const brandKey = (brandOrgId: string | null): string => brandOrgId ?? TOMORROW_KEY;

/** What counts the emails of Tomorrow's own (no brand) in the counts by brand. */
const countKey = (brandOrgId: string | null) => brandOrgId ?? TOMORROW_KEY;

export interface BrandEntry {
  /** `null` is Tomorrow. */
  id: string | null;
  name: string;
  mine: boolean;
}

/**
 * The brands of the list: those of the user's crews first, then Tomorrow's own, then every other, each part by name
 * (crm gives them by name); narrowed by the lens and by part of the name, whatever its case and accents.
 */
export function brandEntries(
  brands: readonly { id: string; name: string; mine: boolean }[],
  tomorrow: string,
  { shows, q }: { shows: (brandOrgId: string | null) => boolean; q?: string | undefined },
): BrandEntry[] {
  const all: BrandEntry[] = [
    ...brands.filter((b) => b.mine),
    { id: null, name: tomorrow, mine: false },
    ...brands.filter((b) => !b.mine),
  ];
  const wanted = q ? fold(q) : "";
  return all.filter((b) => shows(b.id) && (!wanted || fold(b.name).includes(wanted)));
}

const fold = (text: string) =>
  text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase()
    .trim();

/** How many emails each brand has, Tomorrow's own under `tomorrow`. */
export function emailCounts(templates: readonly { brandOrgId: string | null }[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const t of templates) counts.set(countKey(t.brandOrgId), (counts.get(countKey(t.brandOrgId)) ?? 0) + 1);
  return counts;
}

export const emailCountOf = (counts: Map<string, number>, brandOrgId: string | null) =>
  counts.get(countKey(brandOrgId)) ?? 0;

export interface CurrentCampaign {
  id: string;
  name: string;
  startDate: string | null;
  endDate: string | null;
}

/**
 * The campaign of the moment of each brand: the first of Today's (running or to come, the first to start first) that
 * invites to it. A campaign whose brands are not read yet is skipped, and the next one may stand in meanwhile.
 */
export function campaignByBrand(
  campaigns: readonly CurrentCampaign[],
  brandsOf: (campaignId: string) => readonly { id: string }[] | undefined,
): Map<string, CurrentCampaign> {
  const current = new Map<string, CurrentCampaign>();
  for (const campaign of campaigns) {
    for (const brand of brandsOf(campaign.id) ?? []) if (!current.has(brand.id)) current.set(brand.id, campaign);
  }
  return current;
}

const cssName = (role: KitColourRole) => role.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

/**
 * The colours of a kit as the custom properties `.guestlist-kit` reads (styles.css): `--kit-l-<role>` of the light
 * scheme and `--kit-d-<role>` of the dark one, the stylesheet picks by the theme of the app.
 */
export function kitThemeStyle(kit: { light: KitPalette; dark: KitPalette }): Record<string, string> {
  const style: Record<string, string> = {};
  for (const role of kitColourRoles) {
    style[`--kit-l-${cssName(role)}`] = kit.light[role];
    style[`--kit-d-${cssName(role)}`] = kit.dark[role];
  }
  return style;
}
